import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { ServiceLeadForm } from '@/components/ServiceLeadForm';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Sparkles, ExternalLink, ArrowRight, Check, Star, Clock, Shield, Phone, Award } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Images modernes - équipes en action
const HERO_IMAGE = "https://images.pexels.com/photos/6195131/pexels-photo-6195131.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const CONTENT_IMAGE_1 = "https://images.pexels.com/photos/6195951/pexels-photo-6195951.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const CONTENT_IMAGE_2 = "https://images.unsplash.com/photo-1742483359033-13315b247c74?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxob21lJTIwaW50ZXJpb3IlMjBjbGVhbmluZyUyMHRlYW0lMjBhY3Rpb258ZW58MHx8fHwxNzc0MTI5MDU1fDA&ixlib=rb-4.1.0&q=85";

export const NettoyagePage = () => {
  const navigate = useNavigate();

  const services = [
    { title: 'Nettoyage canapé', description: 'Redonner vie à votre canapé', link: '/nettoyage/canape' },
    { title: 'Nettoyage terrasse', description: 'Terrasse impeccable', link: '/nettoyage/terrasse' },
    { title: 'Nettoyage vitres', description: 'Vitres transparentes', link: '/nettoyage/vitres' },
    { title: 'Fin de chantier', description: 'Nettoyage après travaux', link: '/nettoyage/fin-de-chantier' },
    { title: 'Après bail', description: 'Remise en état complète', link: '/nettoyage/apres-bail' }
  ];

  const questions = [
    { id: 'service', label: 'Quel service vous intéresse ?', type: 'select', options: ['Canapé', 'Terrasse', 'Vitres', 'Fin de chantier', 'Après bail', 'Autre'] },
    { id: 'type_bien', label: 'Quel type de bien ?', type: 'select', options: ['Appartement', 'Maison', 'Chalet', 'Bureau', 'Local commercial'] },
    { id: 'surface', label: 'Surface ou volume concerné ?', type: 'text', placeholder: 'Ex: 80 m² ou 3.5 pièces' },
    { id: 'urgence', label: 'Intervention rapide souhaitée ?', type: 'select', options: ['Sous 48h', 'Cette semaine', 'Semaine prochaine', 'Flexible'] }
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="nettoyage-page">
      <Header />
      
      {/* ============================================
          PILIER 1: ATTENTION - Hero immersif avec équipe en action
          ============================================ */}
      <section className="hero-section relative min-h-[70vh] sm:min-h-[80vh] flex items-center">
        <img 
          src={HERO_IMAGE} 
          alt="Équipe de nettoyage professionnelle en action" 
          className="hero-image-bg"
        />
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8 py-10 sm:py-16 md:py-20">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
              <Sparkles className="h-7 w-7 sm:h-8 sm:w-8 md:h-10 md:w-10 text-white" />
              <Badge className="bg-white/20 text-white border-white/30 backdrop-blur-sm text-xs sm:text-sm">
                Homely Nettoyage
              </Badge>
            </div>
            
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 sm:mb-6 text-white leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
              Un bien propre, valorisé et prêt à être utilisé
            </h1>
            
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-white/90 mb-4 sm:mb-6 max-w-2xl">
              Canapé, terrasse, vitres, fin de chantier, après bail : nos équipes professionnelles 
              redonnent à votre bien tout son éclat. Résultat garanti.
            </p>

            {/* Métriques clés */}
            <div className="flex flex-wrap gap-2 sm:gap-3 md:gap-4 mb-5 sm:mb-8">
              <div className="flex items-center gap-1.5 sm:gap-2 bg-white/10 backdrop-blur-sm px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-full">
                <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-green-400" />
                <span className="text-white text-xs sm:text-sm font-semibold">Sous 48h</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 bg-white/10 backdrop-blur-sm px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-full">
                <Star className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-400" />
                <span className="text-white text-xs sm:text-sm">4.9/5</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 bg-white/10 backdrop-blur-sm px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-full">
                <Shield className="h-4 w-4 sm:h-5 sm:w-5 text-blue-400" />
                <span className="text-white text-xs sm:text-sm">Garantie satisfaction</span>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Button 
                size="lg" 
                className="btn-premium h-12 sm:h-14 px-6 sm:px-10 text-sm sm:text-base rounded-full w-full sm:w-auto"
                onClick={() => document.getElementById('form').scrollIntoView({ behavior: 'smooth' })}
                data-testid="nettoyage-cta-primary"
              >
                Obtenir un devis gratuit
                <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="h-12 sm:h-14 px-6 sm:px-8 text-sm sm:text-base rounded-full bg-white/10 border-white/30 text-white hover:bg-white hover:text-[var(--anthracite)] backdrop-blur-sm gap-2 w-full sm:w-auto"
                onClick={() => window.open('https://www.homely-nettoyage.ch', '_blank')}
              >
                <ExternalLink className="h-4 w-4" />
                <span className="hidden xs:inline">homely-nettoyage.ch</span>
                <span className="xs:hidden">Site web</span>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          PILIER 2: ÉMOTION - Le problème ressenti
          ============================================ */}
      <section className="py-12 sm:py-16 md:py-20">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 md:gap-12 items-center">
            <div>
              <Badge className="mb-3 sm:mb-4 bg-orange-100 text-orange-700 text-xs sm:text-sm">Le constat</Badge>
              
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Un bien mal entretenu perd de la valeur... et vous fait perdre du temps
              </h2>
              
              <p className="text-sm sm:text-base md:text-lg text-muted-foreground mb-4 sm:mb-6">
                Taches sur le canapé, terrasse envahie par la mousse, vitres opaques... 
                Vous avez essayé de nettoyer vous-même, mais le résultat n'est jamais à la hauteur. 
                Et le temps passé, c'est du temps perdu ailleurs.
              </p>

              <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                {[
                  { icon: Clock, title: 'Temps perdu', desc: 'Des heures à frotter sans résultat professionnel' },
                  { icon: '😤', title: 'Frustration', desc: 'Produits grand public inefficaces sur les taches tenaces' },
                  { icon: '💸', title: 'Coût caché', desc: 'Remplacement anticipé de meubles mal entretenus' },
                  { icon: '📉', title: 'Valeur en baisse', desc: 'Un bien négligé se vend ou se loue moins cher' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3 sm:gap-4 p-3 sm:p-4 bg-secondary/30 rounded-lg">
                    {typeof item.icon === 'string' ? (
                      <span className="text-2xl">{item.icon}</span>
                    ) : (
                      <item.icon className="h-6 w-6 text-orange-500 flex-shrink-0 mt-1" />
                    )}
                    <div>
                      <p className="font-semibold">{item.title}</p>
                      <p className="text-sm text-muted-foreground">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-lg font-semibold" style={{ color: 'var(--sage-dark)' }}>
                Et si vous confiiez cette charge à des experts équipés pour le faire ?
              </p>
            </div>

            <div className="relative">
              <img 
                src={CONTENT_IMAGE_1} 
                alt="Professionnel du nettoyage avec équipement moderne" 
                className="rounded-2xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-xl shadow-lg max-w-xs">
                <p className="font-bold text-3xl" style={{ color: 'var(--sage)' }}>98%</p>
                <p className="text-sm text-muted-foreground">de nos clients nous recommandent</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          PILIER 3: MÉMOIRE - Nos services détaillés
          ============================================ */}
      <section className="py-20 gradient-premium">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4" style={{ backgroundColor: 'var(--sage)', color: 'white' }}>Nos services</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Un service de nettoyage pour chaque besoin
            </h2>
            <div className="section-divider"></div>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Équipements professionnels, produits écologiques, équipes formées : 
              nous intervenons sur tout type de surface et de salissure.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-16">
            {services.map((service, idx) => (
              <Card 
                key={idx} 
                className="card-premium cursor-pointer text-center p-6 hover:border-[var(--sage)] transition-all group"
                onClick={() => navigate(service.link)}
                data-testid={`service-card-${idx}`}
              >
                <CardTitle className="text-lg mb-2 group-hover:text-[var(--sage)]">{service.title}</CardTitle>
                <CardDescription>{service.description}</CardDescription>
              </Card>
            ))}
          </div>

          {/* Image + Liste avantages */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <img 
              src={CONTENT_IMAGE_2} 
              alt="Nettoyage professionnel de tapis en action" 
              className="rounded-2xl shadow-xl w-full"
            />
            <div>
              <h3 className="text-2xl font-bold mb-6">Ce qui nous différencie :</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  'Équipements professionnels',
                  'Produits éco-responsables',
                  'Équipes formées et assurées',
                  'Devis gratuit sous 24h',
                  'Intervention sous 48h',
                  'Garantie satisfaction',
                  'Tarifs transparents',
                  'Suivi personnalisé',
                  'Facturation claire',
                  'Service après-vente réactif'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span className="text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          PILIER 4: DÉCISION - Preuves sociales
          ============================================ */}
      <section className="py-20">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          
          {/* Stats bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 p-8 rounded-2xl mb-16" style={{ backgroundColor: 'var(--sage)' }}>
            {[
              { value: '2\'500+', label: 'Nettoyages réalisés' },
              { value: '48h', label: 'Délai d\'intervention' },
              { value: '98%', label: 'Taux de satisfaction' },
              { value: '4.9/5', label: 'Note moyenne' }
            ].map((stat, idx) => (
              <div key={idx} className="text-center text-white">
                <p className="text-4xl font-bold mb-1">{stat.value}</p>
                <p className="text-sm text-white/80">{stat.label}</p>
              </div>
            ))}
          </div>

          {/* Témoignages */}
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Ils nous font confiance
            </h2>
            <div className="section-divider"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {[
              { 
                name: 'Marie D.', 
                city: 'Lausanne', 
                service: 'Nettoyage canapé',
                text: 'Mon canapé en tissu clair était couvert de taches après 3 ans. Résultat bluffant, on dirait qu\'il est neuf ! Équipe ponctuelle et soigneuse.' 
              },
              { 
                name: 'Patrick R.', 
                city: 'Genève', 
                service: 'Après bail',
                text: 'J\'appréhendais l\'état des lieux de sortie. Homely a tout nettoyé en une journée. J\'ai récupéré l\'intégralité de ma garantie de loyer.' 
              },
              { 
                name: 'Famille Weber', 
                city: 'Montreux', 
                service: 'Fin de chantier',
                text: 'Après nos travaux de rénovation, la poussière était partout. Leur équipe a tout remis en ordre en quelques heures. Professionnel et efficace.' 
              }
            ].map((review, idx) => (
              <div key={idx} className="card-premium p-6">
                <div className="flex gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-muted-foreground mb-4 italic">"{review.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[var(--sage)] flex items-center justify-center text-white font-bold">
                    {review.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-semibold text-sm">{review.name}</p>
                    <p className="text-xs text-muted-foreground">{review.service} • {review.city}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Avantages rapides */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Clock, title: 'Rapidité', desc: 'Intervention sous 48h partout en Suisse romande' },
              { icon: Award, title: 'Qualité', desc: 'Produits professionnels et équipes certifiées' },
              { icon: Shield, title: 'Garantie', desc: 'Satisfaction garantie ou repasse offerte' }
            ].map((item, i) => (
              <div key={i} className="text-center p-8 rounded-2xl" style={{ backgroundColor: 'rgba(168, 179, 156, 0.1)' }}>
                <item.icon className="h-10 w-10 mx-auto mb-4" style={{ color: 'var(--sage)' }} />
                <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--sage-dark)' }}>{item.title}</h3>
                <p className="text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          PILIER 5: ACTION - Formulaire de conversion
          ============================================ */}
      <section className="py-20" style={{ backgroundColor: 'var(--anthracite)' }} id="form">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="text-white">
              <Badge className="mb-4 bg-[var(--sage)]">Devis gratuit</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Retrouvez un bien impeccable
              </h2>
              <p className="text-white/70 mb-8 text-lg">
                Décrivez votre besoin et recevez un devis personnalisé sous 24h. 
                Intervention rapide, résultat garanti.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  'Devis détaillé et transparent',
                  'Intervention sous 48h',
                  'Équipements professionnels',
                  'Garantie satisfaction ou repasse'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-[var(--sage)] flex items-center justify-center">
                      <Check className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-white/90">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-2xl">
              <ServiceLeadForm service="nettoyage" pole="nettoyage" questions={questions} />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section pour SEO */}
      <section className="py-16 border-t">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold mb-8 text-center">Questions fréquentes</h2>
          <div className="max-w-3xl mx-auto space-y-6">
            {[
              { q: 'Quels types de nettoyage proposez-vous ?', a: 'Nous proposons le nettoyage de canapés, terrasses, vitres, fin de chantier et après bail. Chaque service utilise des équipements et produits adaptés.' },
              { q: 'Sous quel délai intervenez-vous ?', a: 'Nous intervenons généralement sous 48h partout en Suisse romande. Pour les urgences, contactez-nous par téléphone.' },
              { q: 'Quels produits utilisez-vous ?', a: 'Nous privilégions des produits professionnels éco-responsables, efficaces et respectueux de vos surfaces et de l\'environnement.' },
              { q: 'Proposez-vous une garantie ?', a: 'Oui, nous offrons une garantie satisfaction. Si le résultat ne vous convient pas, nous repassons gratuitement.' }
            ].map((faq, idx) => (
              <div key={idx} className="p-6 bg-secondary/20 rounded-lg">
                <h3 className="font-semibold mb-2">{faq.q}</h3>
                <p className="text-muted-foreground">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};
